<template>
  <AttrGroup v-if="isAOD && supportGlobalMask" title="全局蒙层">
    <div class="px-2.5 mt-2">
      <div class="text-xs opacity-50 mb-2">
        上传蒙层图片，将覆盖在息屏表盘最上层，蒙层图片要求为黑色底 + 透明度。
      </div>
      <ImagePicker
        :size="48"
        :icon-size="20"
        :border-color="'rgba(255, 255, 255, 0.2)'"
        :img="maskImagePreview"
        @delete="onDeleteMask"
        @change="onUploadMask"
      />
    </div>
  </AttrGroup>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useEditorStore } from '@/store/use-editor-store';
import { useWatchfaceStore } from '@/store/use-watchface-store';
import { Shape, ShapeType } from '@/services/shape';
import { useWatchfaceAodPixel } from '@/hooks';
import { getImageBase64 } from '@/utils/fs/image.utils';
import { emitter, EventType } from '@/utils/emitter.utils';
import { Modal } from 'ant-design-vue';
import { round } from 'lodash-es';
import { canUse } from '@/utils/can-use.utils';

const editorStore = useEditorStore();
const watchfaceStore = useWatchfaceStore();
const { getRule } = useWatchfaceAodPixel();

const isAOD = computed(() => editorStore.theme?.type === 'AOD');
const supportGlobalMask = computed(() => canUse('global-mask').valid);

// 蒙层标记名称
const MASK_NAME = '_aod_global_mask';

// 查找当前主题中的蒙层元素
const maskShape = computed(() => {
  const theme = editorStore.theme;
  if (!theme) return undefined;
  return theme.children.find((layer) => layer.attrs._maskFlag === MASK_NAME);
});

// 获取蒙层图片预览
const maskImagePreview = computed(() => {
  if (!maskShape.value) return '';
  const shape = Shape.getShape(maskShape.value.id);
  if (!shape) return '';
  const colorId = editorStore.themeColor?.id ?? '';
  const images = shape.getResources({
    resourceName: 'pointer0',
    colorGroup: colorId,
    base64: true
  });
  return images?.[0] ?? '';
});

/**
 * 校验蒙层图片（复用 AOD 校验工具的逻辑）
 * @returns 校验通过返回 true，不通过返回 false
 */
const validateMaskImage = async (realpath: string): Promise<number> => {
  const rule = getRule();
  if (!rule) return true; // 没有规则则不校验

  const base64 = await getImageBase64(realpath);
  if (!base64) return true;

  const img = new Image();
  img.src = base64;
  await new Promise((resolve, reject) => {
    img.onload = resolve;
    img.onerror = reject;
  });

  // 校验图片尺寸必须和表盘一致
  const { width: watchWidth, height: watchHeight } = watchfaceStore.size;
  if (img.width !== watchWidth || img.height !== watchHeight) {
    Modal.warning({
      title: '蒙层图片校验不通过',
      content: `图片尺寸 ${img.width}×${img.height} 与表盘尺寸 ${watchWidth}×${watchHeight} 不一致`,
      okText: '知道了'
    });
    return 0;
  }

  const canvas = document.createElement('canvas');
  canvas.width = img.width;
  canvas.height = img.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return true;
  ctx.drawImage(img, 0, 0);

  const data = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
  let allPixels = 0;
  let notBlackPixels = 0;
  let notSameAlpha = 0;
  let blackPixels = 0;
  const centerX = Math.floor(img.width / 2);
  const centerY = Math.floor(img.height / 2);
  const centerIndex = (centerY * img.width + centerX) * 4;
  const centerAlpha = data[centerIndex + 3];
  let transparent = 0;

  for (let i = 0; i < data.length; i += 4) {
    allPixels++;
    const R = data[i];
    const G = data[i + 1];
    const B = data[i + 2];
    const A = data[i + 3];
    const isBlack = R === 0 && G === 0 && B === 0;
    if (A === 0) {
      // 全透明像素
      transparent++;
    } else {
      // 非透明像素：判断是否与中心点alpha不同
      if (A !== centerAlpha) {
        notSameAlpha++;
      }
      // 非透明且非黑像素
      if (!isBlack) {
        notBlackPixels++;
      } else {
        // 全黑不透明像素
        if (A === 255) {
          blackPixels++;
        }
      }
    }
  }

  const errors: string[] = [];

  // 校验非黑底+透明度的像素数量
  if (notBlackPixels / allPixels > 0.05 || blackPixels / allPixels > 0.77) {
    Modal.warning({
      title: '蒙层图片校验不通过',
      content: `蒙层图片不满足黑色底加透明度的要求`,
      okText: '知道了'
    });
    return 0;
  }
  if (transparent / allPixels > 0.5) {
    Modal.warning({
      title: '蒙层图片校验不通过',
      content: `蒙层图片不可为纯透明图片`,
      okText: '知道了'
    });
    return 0;
  }
  if (notSameAlpha / allPixels > 0.33) {
    Modal.warning({
      title: '蒙层图片校验不通过',
      content: `蒙层图片的透明度不统一`,
      okText: '知道了'
    });
    return 0;
  }

  return centerAlpha;
};

// 上传蒙层图片
const onUploadMask = async (realpath: string) => {
  const theme = editorStore.theme;
  if (!theme) return;

  // 校验图片
  const valid = await validateMaskImage(realpath);
  if (!valid) return;

  const { width, height } = watchfaceStore.size;
  const colorId = editorStore.themeColor?.id ?? '';

  // 如果已有蒙层元素，直接更新图片
  if (maskShape.value) {
    const shape = Shape.getShape(maskShape.value.id);
    if (shape) {
      await shape.uploadResource(
        { resourceName: 'pointer0', colorGroup: colorId, realpath },
        true,
        true
      );
      // 更新蒙层像素透明度（用于 AOD 像素校验，按配色存储）
      shape.setAttrs({
        _maskPixelAlpha: {
          ...(shape.attrs._maskPixelAlpha ?? {}),
          [colorId]: valid / 255
        }
      });
    }
    return;
  }

  // 创建新的图片元素作为蒙层
  const shape = Shape.create(ShapeType.Image, {
    attrs: {
      name: '全局蒙层',
      x: 0,
      y: 0,
      w: width,
      h: height,
      alpha: 1,
      _isLocked: true,
      _maskFlag: MASK_NAME,
      _maskPixelAlpha: { [colorId]: valid / 255 }
    }
  });

  // 上传图片
  await shape.uploadResource(
    { resourceName: 'pointer0', colorGroup: colorId, realpath },
    true,
    true
  );

  // 确保蒙层在最上层（children 的最后一个）
  const children = theme.children;
  const idx = children.findIndex((item) => item.id === shape.id);
  if (idx >= 0 && idx < children.length - 1) {
    const [item] = children.splice(idx, 1);
    children.push(item);
  }

  emitter.emit(EventType['shape:create'], shape.id);
};

// 删除蒙层
const onDeleteMask = () => {
  const theme = editorStore.theme;
  if (!theme || !maskShape.value) return;
  const shapeId = maskShape.value.id;
  theme.children = theme.children.filter((item) => item.id !== shapeId);
  Shape.clearCache();
  emitter.emit(EventType['shape:deleted']);
};
</script>
